import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  TablePagination,
  Fade,
  Zoom,
  alpha,
  useTheme,
  LinearProgress
} from '@mui/material';
import {
  Restore,
  Refresh,
  Info,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  History,
  AccessTime,
  Person,
  Code,
  Replay
} from '@mui/icons-material';
import axios from 'axios';
import { useWebSocketContext } from '../context/WebSocketContext';
import RollbackDialog from './RollbackDialog';

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// Status Badge Component
const StatusBadge = ({ status }) => {
  const theme = useTheme();
  const configs = {
    success: {
      icon: <CheckCircle sx={{ fontSize: 16 }} />,
      label: 'Success',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      bg: alpha('#38ef7d', 0.1),
      border: alpha('#38ef7d', 0.3)
    },
    failed: {
      icon: <ErrorIcon sx={{ fontSize: 16 }} />,
      label: 'Failed',
      gradient: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
      bg: alpha('#eb3349', 0.1),
      border: alpha('#eb3349', 0.3)
    },
    in_progress: {
      icon: <Schedule sx={{ fontSize: 16 }} />,
      label: 'In Progress',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      bg: alpha('#667eea', 0.1),
      border: alpha('#667eea', 0.3)
    }
  };

  const config = configs[status] || configs.in_progress;

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size="small"
      sx={{
        background: config.bg,
        border: `1px solid ${config.border}`,
        fontWeight: 600,
        fontSize: '0.75rem',
        '& .MuiChip-icon': {
          color: 'inherit'
        }
      }}
    />
  );
};

export default function DeploymentHistory({ onMessage }) {
  const theme = useTheme();
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);

  const { subscribe } = useWebSocketContext();

  const fetchDeployments = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/deployments?limit=${rowsPerPage}&offset=${page * rowsPerPage}`,
        { withCredentials: true }
      );
      setDeployments(response.data.deployments || []);
    } catch (error) {
      console.error('Error fetching deployments:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchDeployments();
  }, [fetchDeployments]);

  useEffect(() => {
    const unsubscribe = subscribe('deployment', (message) => {
      if (message.status === 'success' || message.status === 'failed') {
        fetchDeployments();
      }
    });
    return unsubscribe;
  }, [subscribe, fetchDeployments]);

  const handleRollback = (deployment) => {
    setSelectedDeployment(deployment);
    setRollbackDialogOpen(true);
  };

  const confirmRollback = async () => {
    if (!selectedDeployment) return;

    setRollbackLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/deployments/${selectedDeployment.id}/rollback`,
        {},
        { withCredentials: true }
      );

      onMessage({ type: 'success', text: response.data.message });
      setRollbackDialogOpen(false);
      fetchDeployments();
    } catch (error) {
      onMessage({
        type: 'error',
        text: error.response?.data?.error || 'Rollback failed'
      });
    } finally {
      setRollbackLoading(false);
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      // Invalid date, return the string as-is
      return dateStr;
    }
    const now = new Date();
    const diff = now - date;

    // If negative or very large, might be timezone issue - show actual time
    if (diff < 0 || diff > 86400000 * 365) {
      return date.toLocaleString();
    }

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 86400000 * 7) return `${Math.floor(diff / 86400000)}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canRollback = (deployment) => {
    return deployment.status === 'success' &&
           !deployment.is_rollback &&
           deployment.backup_path;
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 8,
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={48} sx={{ color: '#667eea' }} />
        <Typography color="text.secondary" fontWeight={500}>
          Loading deployment history...
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 4,
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box sx={{
            p: 3,
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}>
                <History sx={{ color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Deployment History
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track and manage all deployments
                </Typography>
              </Box>
            </Box>
            <Button
              startIcon={<Refresh />}
              onClick={fetchDeployments}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 2.5
              }}
            >
              Refresh
            </Button>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: alpha(theme.palette.action.hover, 0.3),
                  '& th': {
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: 0.5,
                    color: 'text.secondary',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }
                }}>
                  <TableCell>Deployment</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Deployed By</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Commit</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deployments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box sx={{ py: 6, textAlign: 'center' }}>
                        <History sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No Deployments Yet
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          Deployments will appear here once you start deploying
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  deployments.map((deployment, index) => (
                    <Zoom in timeout={200 + index * 50} key={deployment.id}>
                      <TableRow
                        sx={{
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.03)
                          },
                          '& td': {
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontWeight={600}>
                              #{deployment.id}
                            </Typography>
                            {deployment.is_rollback && (
                              <Chip
                                icon={<Replay sx={{ fontSize: 14 }} />}
                                label="Rollback"
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  background: alpha(theme.palette.warning.main, 0.1),
                                  color: theme.palette.warning.dark,
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={deployment.target}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              borderRadius: 2
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={deployment.status} />
                          {deployment.status === 'in_progress' && (
                            <LinearProgress
                              sx={{
                                mt: 1,
                                borderRadius: 1,
                                height: 3,
                                background: alpha('#667eea', 0.1),
                                '& .MuiLinearProgress-bar': {
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                }
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight={500}>
                              {deployment.deployed_by}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {formatTime(deployment.started_at)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontWeight: 600,
                              color: deployment.build_duration_ms ? 'text.primary' : 'text.disabled'
                            }}
                          >
                            {formatDuration(deployment.build_duration_ms)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {deployment.commit_hash ? (
                            <Tooltip title={deployment.commit_hash} arrow>
                              <Chip
                                icon={<Code sx={{ fontSize: 14 }} />}
                                label={deployment.commit_hash.substring(0, 7)}
                                size="small"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  background: alpha(theme.palette.action.hover, 0.5),
                                  cursor: 'pointer'
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            {canRollback(deployment) && (
                              <Tooltip title="Rollback to this version" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRollback(deployment)}
                                  sx={{
                                    background: alpha(theme.palette.warning.main, 0.1),
                                    color: theme.palette.warning.dark,
                                    '&:hover': {
                                      background: alpha(theme.palette.warning.main, 0.2),
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <Restore fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {deployment.error_message && (
                              <Tooltip title={deployment.error_message} arrow>
                                <IconButton
                                  size="small"
                                  sx={{
                                    background: alpha(theme.palette.error.main, 0.1),
                                    color: theme.palette.error.main,
                                    '&:hover': {
                                      background: alpha(theme.palette.error.main, 0.2)
                                    }
                                  }}
                                >
                                  <Info fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </Zoom>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={-1}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelDisplayedRows={({ from, to }) => `${from}-${to}`}
            sx={{
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              '& .MuiTablePagination-select': {
                fontWeight: 600
              }
            }}
          />
        </Paper>

        <RollbackDialog
          open={rollbackDialogOpen}
          onClose={() => setRollbackDialogOpen(false)}
          onConfirm={confirmRollback}
          deployment={selectedDeployment}
          loading={rollbackLoading}
        />
      </Box>
    </Fade>
  );
}
